import axios, { CancelTokenSource } from 'axios';
import { Currency } from '../../components/common/Form/config';
import client from '../axios';
import {
  ExchangeResponse,
  IExchangeResult,
  IExchangeResultRequest,
  IFeeRequest,
  IFeeResponse,
  ISpreadRequest,
  ISpreadResponse,
  IPromoterRateCreateResponse,
} from './types';

const getExchange = async ({
  currency,
  desCurrency,
  enterpriseId,
}: {
  currency: Currency;
  desCurrency?: Currency;
  enterpriseId: string;
}): Promise<ExchangeResponse> => {
  const url = `rate/?currency=${currency}`;
  if (desCurrency) {
    return client.get(
      `${url}&des_currency=${desCurrency}&enterprise_id=${enterpriseId}`
    );
  }

  return client.get(url);
};

const getExchangeResult = async ({
  rateUuid,
  reverse,
  amount,
  enterpriseId,
  requestFee,
}: IExchangeResultRequest): Promise<{
  cancel: () => void;
  request: Promise<IExchangeResult>;
}> => {
  const source: CancelTokenSource = axios.CancelToken.source();
  const request = client.get(
    `rate/${rateUuid}/exchange/?amount=${amount}&reverse=${reverse}&enterprise_id=${enterpriseId}&fee=${requestFee}`,
    { cancelToken: source.token, isAuthRedirect: true }
  );
  return { request, cancel: () => source.cancel() };
};

const getFeeResult = async ({
  currency,
  reverse,
  amount,
  contactId,
}: IFeeRequest): Promise<{
  cancel: () => void;
  request: Promise<IFeeResponse>;
}> => {
  const source: CancelTokenSource = axios.CancelToken.source();
  const request = client.get(
    `v1/fee/${currency}/?amount=${amount}&reverse=${reverse}&contactId=${contactId}`,
    { cancelToken: source.token, isAuthRedirect: true }
  );
  return { request, cancel: () => source.cancel() };
};

const getForwardValues = async ({
  amount,
  reverse,
  rateUuid,
  date,
  enterpriseId,
}: ISpreadRequest): Promise<ISpreadResponse> =>
  client.get(
    `rate/${rateUuid}/forward/${enterpriseId}/?amount=${amount}&reverse=${reverse}&date=${date}&origin=1`
  );

const apiGetRate = async ({
  currency,
  desCurrency,
  enterpriseId,
}: {
  currency: string;
  desCurrency: string;
  enterpriseId: string;
}): Promise<any> =>
  client.get(
    `realtime/rate/?currency=${currency}&des_currency=${desCurrency}&enterprise_id=${enterpriseId}`
  );

const getFinalExchangeRate = async ({
  currency,
  desCurrency,
  enterpriseId,
}: {
  currency: string;
  desCurrency: string;
  enterpriseId: string;
}): Promise<any> =>
  client.get(
    `realtime/rate/?currency=${currency}&des_currency=${desCurrency}&enterprise_id=${enterpriseId}`
  );

const createPromoterRate = async ({
  promoterId,
  reference,
  promoterRate,
}: {
  promoterId: string;
  reference: string;
  promoterRate: string;
}): Promise<{ data: IPromoterRateCreateResponse }> => {
  try {
    const response = await client.post(`promoters/${promoterId}/rates/`, {
      reference,
      promoterRate,
    });

    return { data: response };
  } catch (error: any) {
    if (error.response?.data) {
      const { detail } = error.response.data;

      if (detail) {
        if (typeof detail === 'object') {
          const firstErrorKey = Object.keys(detail)[0];
          const firstError = detail[firstErrorKey];
          if (Array.isArray(firstError) && firstError.length > 0) {
            throw new Error(firstError[0]);
          }
          throw new Error('Validation error occurred');
        }

        if (typeof detail === 'string') {
          throw new Error(detail);
        }
      }

      throw new Error('An error occurred while creating the promoter rate');
    }

    throw new Error(error.message || 'Network error occurred');
  }
};

const rateSvc = {
  getExchange,
  getExchangeResult,
  getForwardValues,
  apiGetRate,
  getFeeResult,
  getFinalExchangeRate,
  createPromoterRate,
};

export default rateSvc;
